<script lang="ts" setup>
import {computed, ref} from "vue";

import useStore from "@src/stores/store"

import NoConversation from "@src/components/states/empty-states/NoConversation.vue";
import Circle2Lines from "@src/components/states/loading-states/Circle2Lines.vue";
import SearchInput from "@src/components/ui/inputs/SearchInput.vue";
import FadeTransition from "@src/components/ui/transitions/FadeTransition.vue";
import SidebarHeader from "@src/components/views/HomeView/Sidebar/SidebarHeader.vue";
import ArchivedButton from "@src/components/views/HomeView/Sidebar/Conversations/ArchivedButton.vue";
import SessionListHold from "@src/components/shared/session/SessionListHold.vue";
import ChatSession from "@src/types/ChatSession";
import {useChatSessionStore} from "@src/stores/chatSession";
import {permissionsHelper} from "@src/helpers/permissionsHelper";
import IconButton from "@src/components/ui/inputs/IconButton.vue";
import Button from "@src/components/ui/inputs/Button.vue";
import Swal from "sweetalert2";
import SessionService from "@src/services/session";
import InformationIcon from "@src/components/ui/utils/InformationIcon.vue";

const store = useStore();

const sessionStore = useChatSessionStore()
const search = ref<string>('')

const filteredSessions = computed<ChatSession[]>(() => {
    if (!search.value.toLocaleLowerCase()) {
        return sessionStore.holdSessions ?? []
    }

    let response: ChatSession[] = []
    ;(sessionStore.holdSessions ?? []).forEach(s => {
        if (s.costumer?.name?.toLocaleLowerCase().includes(search.value.toLocaleLowerCase())
            || s.costumer?.token.includes(search.value)) {
            response.push(s)
        } else if (s.costumer.contact) {
            if (s.costumer.contact.name?.toLocaleLowerCase().includes(search.value.toLocaleLowerCase())
                || s.costumer.contact.nickname?.toLocaleLowerCase().includes(search.value.toLocaleLowerCase())
                || s.costumer.contact.reference?.toLocaleLowerCase().includes(search.value.toLocaleLowerCase())) {
                response.push(s)
            }
        }
    })
    return response
});

// determines whether the archive is open or not
const openArchive = ref(false);

const openActionsBulk = () => {
    sessionStore.actionSessions = []
    sessionStore.openActionsBulk = !sessionStore.openActionsBulk
}

const bulkFinish = async () => {

    if (sessionStore.actionSessions.length === 0) {
        return
    }

    const result = await Swal.fire({
        title: 'Finalizar Atendimentos',
        text: 'Você tem certeza que deseja finalizar esses atendimentos?',
        icon: 'warning',

        showCancelButton: true,
        confirmButtonText: 'Finalizar',
        cancelButtonText: 'Cancelar',

        confirmButtonColor: '#e36464',
        cancelButtonColor: '#6b7280',

        reverseButtons: true
    })

    if (result.isConfirmed) {

        const response = await SessionService.bulkFinishSessions(
            sessionStore.actionSessions
        )

        sessionStore.actionSessions = []
        sessionStore.openActionsBulk = !sessionStore.openActionsBulk
    }
}


</script>

<template>
    <div>
        <SidebarHeader>
            <!--title-->
            <template v-slot:title>
              Em espera
              <InformationIcon title="Em espera" text="Acesse os atendimentos que aguardam um operador." class="mb-2" ></InformationIcon>
            </template>
            <template v-slot:actions>
                <div class="flex items-center">
                    <IconButton
                        aria-label="filter"
                        :class="[ 'cursor-pointer ic-btn-ghost-primary w-7 h-7',
                                { 'bg-gray-200 dark:bg-gray-600': sessionStore.openActionsBulk }
                        ]"
                        title="Selecionar atendimento"
                        @click="openActionsBulk"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" height="22px" viewBox="0 -960 960 960" width="22px" fill="#6A7282" class="rotate-90"><path d="M240-400q-33 0-56.5-23.5T160-480q0-33 23.5-56.5T240-560q33 0 56.5 23.5T320-480q0 33-23.5 56.5T240-400Zm240 0q-33 0-56.5-23.5T400-480q0-33 23.5-56.5T480-560q33 0 56.5 23.5T560-480q0 33-23.5 56.5T480-400Zm240 0q-33 0-56.5-23.5T640-480q0-33 23.5-56.5T720-560q33 0 56.5 23.5T800-480q0 33-23.5 56.5T720-400Z"/></svg>
                    </IconButton>
                </div>
            </template>

        </SidebarHeader>

        <!--search bar-->
        <div class="px-5 xs:pb-6 md:pb-5">
            <SearchInput
                :value="search"
                @value-changed="value => {search = value}"
            />
        </div>

        <div v-if="filteredSessions.length > 0" class="flex w-full text-center mb-4">
            <h1 class="w-full heading-2 text-gray-400">{{ filteredSessions.length }}
                {{ filteredSessions.length === 1 ? 'sessão' : 'sessões' }} em espera</h1>
        </div>
        <!--select session actions-->
        <div v-if="sessionStore.openActionsBulk" class="mb-3 ml-5">
            <div v-if="sessionStore.actionSessions.length > 0">
                <div class="flex items-center">
                    <button  @click="bulkFinish" class="flex justify-center rounded-md py-3 w-full mr-2 bg-red-400 text-gray-100 text-sm font-semibold cursor-pointer items-center">Finalizar conversas
                        <svg class="ml-3" xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#e3e3e3"><path d="m336-280-56-56 144-144-144-143 56-56 144 144 143-144 56 56-144 143 144 144-56 56-143-144-144 144Z"/></svg>
                    </button>
                </div>
            </div>

            <div class="flex items-center mt-4">
                <label class="ios-checkbox blue">
                    <input   type="checkbox"
                             :checked="filteredSessions.length > 0 && filteredSessions.every(s => sessionStore.actionSessions.includes(s.id))"
                             @change="sessionStore.toggleAllActionSessions(filteredSessions.map(s => s.id))"
                    />
                    <div class="checkbox-wrapper">
                        <div class="checkbox-bg"></div>
                        <svg fill="none" viewBox="0 0 24 24" class="checkbox-icon">
                            <path
                                stroke-linejoin="round"
                                stroke-linecap="round"
                                stroke-width="3"
                                stroke="currentColor"
                                d="M4 12L10 18L20 6"
                                class="check-path"
                            ></path>
                        </svg>
                    </div>
                </label>
                <p class="pl-3 heading-2 text-gray-500 dark:text-gray-300">Selecionar todas as conversas</p>
            </div>
        </div>

        <!--conversations-->
        <div
            aria-label="conversations"
            class="w-full h-full scroll-smooth"
            role="list"
            style="overflow-x: visible; overflow-y: scroll"
        >
            <Circle2Lines
                v-for="item in 6"
                v-if="store.status === 'loading' || store.delayLoading"
            />

            <div v-else>
                <ArchivedButton
                    v-if="store.archivedConversations.length > 0"
                    :open="openArchive"
                    @click="openArchive = !openArchive"
                />

                <div
                    v-if="
                    store.status === 'success' &&
                    !store.delayLoading &&
                    sessionStore.holdSessions
                  "
                >
                    <FadeTransition>
                        <SessionListHold :sessions="filteredSessions"/>
                    </FadeTransition>
                </div>

                <div v-else>
                    <NoConversation v-if="store.archivedConversations.length === 0"/>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.checkbox-container {
    display: flex;
    gap: 20px;
    padding: 20px;
    background: #f8fafc;
    border-radius: 16px;
    box-shadow:
        0 4px 6px -1px rgba(0, 0, 0, 0.1),
        0 2px 4px -2px rgba(0, 0, 0, 0.05);
}

.ios-checkbox {
    --checkbox-size: 22px;
    --checkbox-color: #3b82f6;
    --checkbox-bg: #dbeafe;
    --checkbox-border: #93c5fd;

    position: relative;
    display: inline-block;
    cursor: pointer;
    user-select: none;
    -webkit-tap-highlight-color: transparent;
}

.ios-checkbox input {
    display: none;
}

.checkbox-wrapper {
    position: relative;
    width: var(--checkbox-size);
    height: var(--checkbox-size);
    border-radius: 8px;
    transition: transform 0.2s ease;
}

.checkbox-bg {
    position: absolute;
    inset: 0;
    border-radius: 8px;
    border: 2px solid var(--checkbox-border);
    transition: all 0.2s ease;
}

.checkbox-icon {
    position: absolute;
    inset: 0;
    margin: auto;
    width: 80%;
    height: 80%;
    color: white;
    transform: scale(0);
    transition: all 0.2s ease;
}

.check-path {
    stroke-dasharray: 40;
    stroke-dashoffset: 40;
    transition: stroke-dashoffset 0.3s ease 0.1s;
}

/* Checked State */
.ios-checkbox input:checked + .checkbox-wrapper .checkbox-bg {
    background: var(--checkbox-color);
    border-color: var(--checkbox-color);
}

.ios-checkbox input:checked + .checkbox-wrapper .checkbox-icon {
    transform: scale(1);
}

.ios-checkbox input:checked + .checkbox-wrapper .check-path {
    stroke-dashoffset: 0;
}

/* Hover Effects */
.ios-checkbox:hover .checkbox-wrapper {
    transform: scale(1.05);
}

/* Active Animation */
.ios-checkbox:active .checkbox-wrapper {
    transform: scale(0.95);
}

/* Focus Styles */
.ios-checkbox input:focus + .checkbox-wrapper .checkbox-bg {
    box-shadow: 0 0 0 4px var(--checkbox-bg);
}

/* Color Themes */
.ios-checkbox.blue {
    --checkbox-color: #7C8CF5;
    --checkbox-bg: #dbeafe;
    --checkbox-border: #7C8CF5;
}

/* Animation */
@keyframes bounce {
    0%,
    100% {
        transform: scale(1);
    }
    50% {
        transform: scale(1.1);
    }
}

.ios-checkbox input:checked + .checkbox-wrapper {
    animation: bounce 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

</style>